// src/api/widgetVersionService.js
import axios from 'axios';
import apiClient from './axiosConfig'; // For calls requiring User JWT

const API_URL = process.env.REACT_APP_API_URL || '';

// Standardized error handler
const handleApiError = (error, operation) => {
  console.error(
    `${error.config?.method?.toUpperCase()} ${error.config?.url} – Error ${operation}:`,
    error
  );
  let message = 'An unknown error occurred.';
  const status = error.response?.status;

  if (error.response?.data) {
    const d = error.response.data;
    if (typeof d.detail === 'string') {
      message = d.detail;
    } else if (Array.isArray(d.detail)) {
      message = d.detail
        .map((e) => {
          const loc = e.loc?.slice(1).join('.') || 'field';
          return `${loc}: ${e.msg}`;
        })
        .join('; ');
      if (!message) message = 'Validation error';
    } else if (typeof d === 'string' && d.length < 500 && !d.trim().startsWith('<')) {
      message = d;
    } else if (d.message && typeof d.message === 'string') {
      message = d.message;
    } else if (status) {
      message = `Request failed with status code ${status}`;
    }
    if (typeof message === 'string' && message.trim().startsWith('<')) {
         message = `Received an HTML error page (status ${status || 'unknown'}) during ${operation}.`;
     }
  } else if (error.request) {
    message = `No response from server during ${operation}. Check network connection.`;
  } else if (error.message) {
    message = error.message;
  }
  return { isError: true, message, status, originalError: error };
};

export const widgetVersionService = {
  /**
   * GET /api/v1/widgets/by_template/{templateId}
   * Lists all versions for a specific widget template.
   */
  listVersionsByTemplate: async (templateId) => {
     if (!templateId) return { isError: true, message: "Template ID required.", status: 400 };
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/widgets/by_template/${templateId}`
      );
      return data;
    } catch (err) {
      return handleApiError(err, `listing versions for template ${templateId}`);
    }
  },

  /**
   * GET /api/v1/widgets/by_agent/{agentId}
   * Lists widget versions linked to a specific agent.
   */
  getVersionsByAgent: async (agentId) => {
     if (!agentId) return { isError: true, message: "Agent ID required.", status: 400 };
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/widgets/by_agent/${agentId}`
      );
      return Array.isArray(data) ? data : [];
    } catch (err) {
      if (err.response?.status === 404) {
        return []; // Return empty array if agent has no versions or agent not found for user
      }
      return handleApiError(err, `listing versions for agent ${agentId}`);
    }
  },

  /**
   * GET /api/v1/widgets/{versionId}
   * Fetches details for a specific widget version.
   */
  getVersion: async (versionId) => {
     if (!versionId) return { isError: true, message: "Version ID required.", status: 400 };
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/widgets/${versionId}`
      );
      return data;
    } catch (err) {
      return handleApiError(err, `fetching version ${versionId}`);
    }
  },

  /**
   * POST /api/v1/widgets
   * Creates a new widget version.
   */
  createVersion: async (versionData) => {
    try {
      const {
        widget_template_id, agent_id, js_content, name, description, active,
        widgetTitle, placeholderWelcome, welcomeStrategy, defaultWelcome,
        ruleBasedWelcome, llmWelcomePrompt, llmWelcomeModel, collapsedMessage,
        themeColor, position, contextPrepending, botAvatarImage, websiteName,
        showThinkingProcess, streamingEnabled, colors,
        // config_schema, ui_schema, // REMOVED from destructuring as they won't be sent
        ...rest
      } = versionData;

      if (!widget_template_id) return { isError: true, message: "Parent Template ID is missing.", status: 400 };
      if (!agent_id) return { isError: true, message: "Agent ID is required.", status: 400 };

      // parseJsonSchema is no longer needed here if schemas are not sent
      // const parseJsonSchema = (jsonString, defaultValue = {}) => { ... };

      // Construct the 'config' object for the payload
      const configPayload = {
        widgetTitle, placeholderWelcome, welcomeStrategy, defaultWelcome,
        ruleBasedWelcome: welcomeStrategy === 'rules' ? (ruleBasedWelcome || []) : undefined,
        llmWelcomePrompt: welcomeStrategy === 'llm' ? llmWelcomePrompt : undefined,
        llmWelcomeModel: welcomeStrategy === 'llm' ? llmWelcomeModel : undefined,
        collapsedMessage, themeColor, position, contextPrepending,
        botAvatarImage, websiteName, showThinkingProcess, streamingEnabled, colors,
      };
      // Remove undefined keys from configPayload to avoid sending them
      Object.keys(configPayload).forEach(key => configPayload[key] === undefined && delete configPayload[key]);

      const payload = {
        template_id: widget_template_id,
        agent_id,
        js_content: js_content || '',
        version_name: typeof name === 'string' ? name.trim() : name,
        description: typeof description === 'string' ? (description.trim() || null) : (description === null ? null : undefined),
        config: configPayload,
        is_active: active !== undefined ? !!active : false,
      };
      // DO NOT Add schemas if they are not needed by the backend
      // if (config_schema) payload.config_schema = parseJsonSchema(config_schema); // REMOVED
      // if (ui_schema) payload.ui_schema = parseJsonSchema(ui_schema);       // REMOVED

      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const { data } = await apiClient.post(`${API_URL}/api/v1/widgets`, payload);
      return data;
    } catch (err) {
      //  if (err instanceof Error && err.message.startsWith('Invalid JSON format')) { // Less likely now
      //     return { isError: true, message: err.message, status: 400 };
      //  }
      return handleApiError(err, 'creating widget version');
    }
  },

  /**
   * PUT /api/v1/widgets/{versionId}
   * Updates an existing widget version.
   */
  updateVersion: async (versionId, versionData) => {
     if (!versionId) return { isError: true, message: "Version ID required.", status: 400 };
    try {
      const {
        name, description, agent_id, js_content, is_public,
        widgetTitle, placeholderWelcome, welcomeStrategy, defaultWelcome,
        ruleBasedWelcome, llmWelcomePrompt, llmWelcomeModel, collapsedMessage,
        themeColor, position, contextPrepending, botAvatarImage, websiteName,
        showThinkingProcess, streamingEnabled, colors,
        // config_schema, ui_schema, // REMOVED from destructuring
        widget_template_id, is_active,
        ...rest
      } = versionData;

      // parseJsonSchema is no longer needed here if schemas are not sent
      // const parseJsonSchema = (jsonString) => { ... };

      const configPayload = {};
      if (widgetTitle !== undefined) configPayload.widgetTitle = widgetTitle;
      if (placeholderWelcome !== undefined) configPayload.placeholderWelcome = placeholderWelcome;
      if (welcomeStrategy !== undefined) configPayload.welcomeStrategy = welcomeStrategy;
      if (defaultWelcome !== undefined) configPayload.defaultWelcome = defaultWelcome;
      if (welcomeStrategy !== undefined) {
          if (welcomeStrategy === 'rules') {
            if (ruleBasedWelcome !== undefined) configPayload.ruleBasedWelcome = ruleBasedWelcome || [];
            configPayload.llmWelcomePrompt = undefined; 
            configPayload.llmWelcomeModel = undefined;
          } else if (welcomeStrategy === 'llm') {
             if (llmWelcomePrompt !== undefined) configPayload.llmWelcomePrompt = llmWelcomePrompt;
             if (llmWelcomeModel !== undefined) configPayload.llmWelcomeModel = llmWelcomeModel;
             configPayload.ruleBasedWelcome = undefined;
          } else {
             configPayload.ruleBasedWelcome = undefined;
             configPayload.llmWelcomePrompt = undefined;
             configPayload.llmWelcomeModel = undefined;
          }
      } else {
          if (ruleBasedWelcome !== undefined) configPayload.ruleBasedWelcome = ruleBasedWelcome;
          if (llmWelcomePrompt !== undefined) configPayload.llmWelcomePrompt = llmWelcomePrompt;
          if (llmWelcomeModel !== undefined) configPayload.llmWelcomeModel = llmWelcomeModel;
      }
      if (collapsedMessage !== undefined) configPayload.collapsedMessage = collapsedMessage;
      if (themeColor !== undefined) configPayload.themeColor = themeColor;
      if (position !== undefined) configPayload.position = position;
      if (contextPrepending !== undefined) configPayload.contextPrepending = contextPrepending;
      if (botAvatarImage !== undefined) configPayload.botAvatarImage = botAvatarImage;
      if (websiteName !== undefined) configPayload.websiteName = websiteName;
      if (showThinkingProcess !== undefined) configPayload.showThinkingProcess = showThinkingProcess;
      if (streamingEnabled !== undefined) configPayload.streamingEnabled = streamingEnabled;
      if (colors !== undefined) configPayload.colors = colors;
      Object.keys(configPayload).forEach(key => configPayload[key] === undefined && delete configPayload[key]);

      const payload = {};
      if (name !== undefined) {
        if (typeof name === 'string') {
          payload.version_name = name.trim(); 
        }
      }
      if (description !== undefined) {
        if (typeof description === 'string') {
          payload.description = description.trim() || null;
        } else if (description === null) {
          payload.description = null;
        }
      }
      if (agent_id !== undefined) payload.agent_id = agent_id;
      if (js_content !== undefined) payload.js_content = js_content;
      if (is_public !== undefined) payload.is_public = is_public;
      if (Object.keys(configPayload).length > 0) payload.config = configPayload;
      // DO NOT Add schemas if they are not needed by the backend
      // if (config_schema !== undefined) payload.config_schema = parseJsonSchema(config_schema); // REMOVED
      // if (ui_schema !== undefined) payload.ui_schema = parseJsonSchema(ui_schema);          // REMOVED
      
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      if (Object.keys(payload).length === 0) {
           console.warn("updateVersion called with no updatable data. Fetching current version.");
           const currentData = await this.getVersion(versionId);
           return currentData.isError ? currentData : { ...currentData, _message: "No changes submitted." };
      }

      const { data } = await apiClient.put(`${API_URL}/api/v1/widgets/${versionId}`, payload);
      return data;
    } catch (err) {
      //  if (err instanceof Error && err.message.startsWith('Invalid JSON format')) { // Less likely now
      //     return { isError: true, message: err.message, status: 400 };
      //  }
      return handleApiError(err, `updating version ${versionId}`);
    }
  },

  /**
   * DELETE /api/v1/widgets/{versionId}
   * Deletes a widget version.
   */
  deleteVersion: async (versionId) => {
     if (!versionId) return { isError: true, message: "Version ID required.", status: 400 };
    try {
      await apiClient.delete(
        `${API_URL}/api/v1/widgets/${versionId}`
      );
      return { success: true };
    } catch (err) {
      return handleApiError(err, `deleting version ${versionId}`);
    }
  },

  /**
   * POST /api/v1/widgets/{versionId}/activate
   * Sets this version as the active one for its template.
   */
  activateVersion: async (versionId) => {
     if (!versionId) return { isError: true, message: "Version ID required.", status: 400 };
    try {
      const { data } = await apiClient.post(
        `${API_URL}/api/v1/widgets/${versionId}/activate`
      );
      return data;
    } catch (err) {
      return handleApiError(err, `activating version ${versionId}`);
    }
  },

  /**
   * POST /api/v1/widgets/versions/{versionId}/issue-wat
   * Issues a Widget Access Token (requires Permanent API Key).
   */
  issueWat: async (versionId, apiKey, domain = null) => {
    if (!versionId) return { isError: true, message: "Version ID required.", status: 400 };
    if (!apiKey) return { isError: true, message: 'API Key is required to issue a WAT.', status: 400 };
    try {
      const body = {};
      if (domain && typeof domain === 'string' && domain.trim()) {
        body.domain = domain.trim();
      }
      const { data } = await axios.post(
        `${API_URL}/api/v1/widgets/versions/${versionId}/issue-wat`,
        body,
        { headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' } }
      );
      return data;
    } catch (err) {
      return handleApiError(err, `issuing WAT for version ${versionId} using API Key`);
    }
  },

  /**
   * POST /api/v1/widgets/versions/{versionId}/refresh-wat
   * Refreshes an existing Widget Access Token (requires the old WAT).
   */
  refreshWat: async (versionId, oldWatToken) => {
    if (!versionId) return { isError: true, message: "Version ID required.", status: 400 };
    if (!oldWatToken) return { isError: true, message: 'Existing WAT is required to refresh it.', status: 400 };
    try {
      const { data } = await axios.post(
        `${API_URL}/api/v1/widgets/versions/${versionId}/refresh-wat`,
        {},
        { headers: { 'Authorization': `Bearer ${oldWatToken}`, 'Content-Type': 'application/json' } }
      );
      return data;
    } catch (err) {
      return handleApiError(err, `refreshing WAT for version ${versionId}`);
    }
  },
};